// src/utils/linkConfidence.js
//
// How each phase of a slot was linked to its GAM request, as exact / strong /
// weak, so a lucky time-window guess never looks like a cryptographic match.
// Shared by the Lifecycle and Slots tabs (components/LinkConfidence.jsx). Pure.
//
//   exact  — cryptographic or 1:1 structural join (correlator, xai, sai, sigh,
//            amzn_b token, hb_cache_id). No time guessing; survives refreshes.
//   strong — structural match (ad unit, size, gpid, Prebid-anchored timing) that
//            is reliable but not provably unique.
//   weak   — linked on a time window only. On busy pages (refreshes, galleries,
//            multiple same-iu slots) this may be the wrong slot — verify vs raw.

export const CONFIDENCE_LEVEL = {
  // exact
  manual: 'exact', xai: 'exact', sai: 'exact', impCorrelator: 'exact',
  sigh: 'exact', correlator: 'exact', cache_id: 'exact', token: 'exact', amzn_b: 'exact',
  impUrl: 'exact',   // captured ping == a URL the mediation GAM response promised
  // strong
  gpid: 'strong', prebid_size: 'strong', prebid_timing: 'strong',
  size_req: 'strong', size_key: 'strong', adUnit: 'strong', bidder_price: 'strong', single: 'strong',
  sdkTelemetry: 'strong',   // Liftoff SDK impression event, time + size gated — weaker than any hard token
  // weak
  time: 'weak', timing: 'weak', proximity: 'weak',
};

const PHASES = [['prebid', 'Prebid'], ['amazon', 'APS'], ['impression', 'Imp'], ['click', 'Click']];
const RANK = { exact: 1, strong: 2, weak: 3 };

// The field carrying each phase's match tier differs by phase. GAM is the anchor
// (the request everything else links to), so it has no match confidence of its own.
function phaseTier(key, p) {
  if (!p || !p.fired) return null;
  if (key === 'prebid') return p.matchedBy || null;
  if (key === 'amazon' || key === 'impression' || key === 'click') return p.matchTier || null;
  return null;
}

export function phaseConfidence(key, p) {
  const tier = phaseTier(key, p);
  if (!tier) return null;
  return CONFIDENCE_LEVEL[tier] || 'weak';
}

/** A chain is only as trustworthy as its loosest join: the weakest linked phase. */
export function lifecycleConfidence(phases) {
  if (!phases) return null;
  let worst = null;
  for (const [k] of PHASES) {
    const lvl = phaseConfidence(k, phases[k]);
    if (lvl && (worst === null || RANK[lvl] > RANK[worst])) worst = lvl;
  }
  return worst;
}

/** [{ key, label, level, tier, matchLabel }] for each linked phase, in pipeline order. */
export function linkConfidenceItems(phases) {
  if (!phases) return [];
  return PHASES
    .map(([key, label]) => ({
      key,
      label,
      level: phaseConfidence(key, phases[key]),
      tier: phaseTier(key, phases[key]),
      matchLabel: key === 'impression' ? (phases.impression && phases.impression.matchLabel) || null : null,
    }))
    .filter((x) => x.level);
}
